import { useState, useEffect } from 'react';

const DataViewer = ({ darkMode = false }) => {
  const [data, setData] = useState({
    focusSessions: [],
    moodHistory: []
  });
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    const focusSessions = JSON.parse(localStorage.getItem('focusSessions') || '[]');
    const moodHistory = JSON.parse(localStorage.getItem('moodHistory') || '[]');
    
    setData({
      focusSessions,
      moodHistory
    });
  };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      localStorage.removeItem('focusSessions');
      localStorage.removeItem('moodHistory');
      setData({
        focusSessions: [],
        moodHistory: []
      });
    }
  };

  const exportData = () => {
    const dataToExport = {
      focusSessions: data.focusSessions,
      moodHistory: data.moodHistory,
      exportDate: new Date().toISOString()
    };
    
    const dataStr = JSON.stringify(dataToExport, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mental-health-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getMoodEmoji = (value) => {
    if (value >= 5) return '😄';
    if (value >= 4) return '😊';
    if (value >= 3) return '😐';
    if (value >= 2) return '😔';
    return '😢';
  };

  const filteredFocusSessions = data.focusSessions.filter(session => {
    if (filter === 'completed') return session.completed;
    if (filter === 'incomplete') return !session.completed;
    return true;
  });

  const sortedData = {
    focusSessions: [...filteredFocusSessions].sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.completedAt || b.timestamp) - new Date(a.completedAt || a.timestamp);
      }
      if (sortBy === 'duration') {
        return (b.duration || 0) - (a.duration || 0);
      }
      return 0;
    }),
    moodHistory: [...data.moodHistory].sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.timestamp) - new Date(a.timestamp);
      }
      if (sortBy === 'mood') {
        return b.value - a.value;
      }
      return 0;
    })
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white font-sans">
          📋 Data Viewer
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300">
          View, manage, and export your mental health data
        </p>
      </div>

      {/* Controls */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="wellness-input"
            >
              <option value="all">All Sessions</option>
              <option value="completed">Completed Only</option>
              <option value="incomplete">Incomplete Only</option>
            </select>
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="wellness-input"
            >
              <option value="date">Sort by Date</option>
              <option value="duration">Sort by Duration</option>
              <option value="mood">Sort by Mood</option>
            </select>
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={exportData}
              className="wellness-button bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600"
            >
              📁 Export Data
            </button>
            <button
              onClick={clearAllData}
              className="wellness-button bg-gradient-to-r from-red-500 to-pink-500 text-white hover:from-red-600 hover:to-pink-600"
            >
              🗑️ Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="wellness-card p-6 text-center">
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
            {data.focusSessions.length}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Total Focus Sessions
          </div>
        </div>
        
        <div className="wellness-card p-6 text-center">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">
            {data.focusSessions.filter(s => s.completed).length}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Completed Sessions
          </div>
        </div>
        
        <div className="wellness-card p-6 text-center">
          <div className="text-3xl font-bold text-pink-600 dark:text-pink-400">
            {data.moodHistory.length}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Mood Entries
          </div>
        </div>
        
        <div className="wellness-card p-6 text-center">
          <div className="text-3xl font-bold text-orange-600 dark:text-orange-400">
            {data.moodHistory.length > 0 
              ? (data.moodHistory.reduce((sum, mood) => sum + mood.value, 0) / data.moodHistory.length).toFixed(1)
              : '0.0'
            }
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Average Mood
          </div>
        </div>
      </div>

      {/* Data Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Focus Sessions */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white font-sans">
            🍅 Focus Sessions ({sortedData.focusSessions.length})
          </h3>
          
          <div className="wellness-card p-0 overflow-hidden">
            {sortedData.focusSessions.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No focus sessions found
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Duration
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {sortedData.focusSessions.map((session, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(session.completedAt || session.timestamp)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {formatDuration(session.duration || 25)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            session.completed 
                              ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                          }`}>
                            {session.completed ? '✅ Completed' : '⏸️ Incomplete'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Mood History */}
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white font-sans">
            😊 Mood History ({sortedData.moodHistory.length})
          </h3>
          
          <div className="wellness-card p-0 overflow-hidden">
            {sortedData.moodHistory.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No mood entries found
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Mood
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                    {sortedData.moodHistory.map((mood, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(mood.timestamp)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl">{mood.emoji}</span>
                            <span className="text-gray-900 dark:text-gray-100">{mood.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-900 dark:text-gray-100">{mood.value}/5</span>
                            <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full"
                                style={{ width: `${(mood.value / 5) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataViewer;
